import { useEffect, useMemo, useRef, useState } from "react";
import "./index.scss";

export type GlobalToastType = "success" | "error" | "info";

export type GlobalToastItemData = {
  id: string;
  title: string;
  description?: string;
  type: GlobalToastType;
  duration: number;
};

type GlobalToastViewportProps = {
  toasts: GlobalToastItemData[];
  onDismiss: (id: string) => void;
};

const GlobalToastItem = ({
  toast,
  onDismiss,
}: {
  toast: GlobalToastItemData;
  onDismiss: (id: string) => void;
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const isClosingRef = useRef(false);

  const iconGlyph = useMemo(() => {
    if (toast.type === "error") {
      return "!";
    }

    if (toast.type === "info") {
      return "i";
    }

    return "✓";
  }, [toast.type]);

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      setIsVisible(true);
    });

    return () => cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (!isClosingRef.current) {
        handleDismiss();
      }
    }, toast.duration);

    return () => window.clearTimeout(timer);
  }, [toast.duration]);

  const handleDismiss = () => {
    if (isClosingRef.current) {
      return;
    }

    isClosingRef.current = true;
    setIsVisible(false);

    window.setTimeout(() => {
      onDismiss(toast.id);
    }, 380);
  };

  return (
    <div
      className={
        isVisible
          ? `global-toast-item ${toast.type} is-visible`
          : `global-toast-item ${toast.type} is-hidden`
      }
      role="status"
      aria-live="polite"
    >
      <div className="global-toast-body">
        <div className="global-toast-icon-col">
          <div className="global-toast-icon">{iconGlyph}</div>
        </div>
        <div className="global-toast-content">
          <div className="global-toast-title">{toast.title}</div>
          {toast.description ? (
            <div className="global-toast-desc">{toast.description}</div>
          ) : null}
        </div>
        <button
          type="button"
          className="global-toast-close"
          aria-label="Dismiss notification"
          onClick={handleDismiss}
        >
          &times;
        </button>
      </div>
      <div className="global-toast-progress-track">
        <div
          className="global-toast-progress-bar"
          style={{ animationDuration: `${toast.duration}ms` }}
        />
      </div>
    </div>
  );
};

export const GlobalToastViewport = ({
  toasts,
  onDismiss,
}: GlobalToastViewportProps) => {
  if (toasts.length === 0) {
    return null;
  }

  return (
    <div className="global-toast-viewport">
      {toasts.map((toast) => (
        <GlobalToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
};
